const floatPattern = /^-{0,1}[0-9]{0,}[.,][0-9]{1,}$/;
const integerPattern = /^-{0,1}[0-9]{1,}$/;

export const minAuthorizeDiff = 0.005;

export function mathFix(value: number, dec: number = 2): number {
    if (dec >= 0) {
        const r = Math.pow(10, dec);
        return Math.round(value * r) / r;
    }
    return value;
}

export function mathAbsFix(value: number, dec: number = 2): number {
    return mathFix(Math.abs(value), dec);
}

export function checkIfCloseToZero(value: number): number {
    if (Math.abs(value) < 1e-2) return 0;
    return value;
}

export function isAccountingZero(value: number): boolean {
    const lValue = Math.round(value * 100) / 100;
    if (Math.abs(lValue)) {
        return false;
    }
    return true;
}

/**
 * @param {number} K0 capital emprunté
 * @param {number} n  nombre de périodes
 * @param {float} t taux d'intérêt pour la période
 * @param {number} dec nombre de décimales dans le résultat (optionnel)
 * @return {float} mensualité
 */
export function mathVpm(K0: number, n: number, t: number, dec: number = 2) {
    if (n === 0) {
        return 0;
    }

    if (t === 0) {
        return mathFix(K0 / n, dec);
    }
    return mathFix((K0 * t) / (1 - Math.pow(1 + t, -n)), dec);
}

export function getStringAsFloat(search: string): number | undefined {
    if (search) {
        const valueS = search.replace(/,/g, ".");
        const value = parseFloat(valueS);
        if (!isNaN(value)) {
            return value;
        }
    }
    return undefined;
}

export function checkInt(x: string): boolean {
    if (x === null || x === undefined || x === "") {
        return false;
    }
    return !!x.match(integerPattern);
}

export function checkFloat(x): boolean {
    if (x === null || x === undefined || x === "") {
        return false;
    }
    return !!x.match(floatPattern);
}

export function checkAmount(x: string): boolean {
    let result = checkFloat(x);
    if (!result) result = checkInt(x);
    return result;
}

export function percentageVariation(
    initialValue: number | undefined | null,
    finalValue: number | undefined | null,
): string {
    if (initialValue === undefined || initialValue === null) {
        return "-";
    }
    if (finalValue === undefined || finalValue === null) {
        return "-";
    }
    if (Math.abs(finalValue) < 1e-2) {
        return "-";
    }
    if (Math.abs(initialValue) < 1e-2) {
        return "-";
    }

    let result = (finalValue - initialValue) / Math.abs(initialValue);
    if (Math.abs(result) < 1e-2) {
        result = 0;
    }

    return new Intl.NumberFormat("fr-FR", {
        style: "percent",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(result);
}
export interface FormatCurrencyOptions {
    dashForZero: boolean;
    currency: string;
    decimals: number;
    appendCharacter: string;
    DC: boolean;
}
export const defaultFormatCurrencyOptions: FormatCurrencyOptions = {
    dashForZero: true,
    currency: "EUR", // ** to hide currency symbol, please set currency to ''
    decimals: 2,
    appendCharacter: undefined, //** ex "%"
    DC: false, // ** to display "D" or "C" at the end of the amount
};
export function newCommonFormatCurrency(
    value: number,
    options: Partial<FormatCurrencyOptions> = undefined,
) {
    options = {
        ...defaultFormatCurrencyOptions,
        ...options,
    };

    if (value === undefined || value === null || value === 0 || Math.abs(value) < 1e-2) {
        return options.dashForZero ? "-" : "";
    }

    const amountOptions: Intl.NumberFormatOptions = {
        minimumFractionDigits: options.decimals,
        maximumFractionDigits: options.decimals,
    };

    if (options.currency?.length && !options.appendCharacter && !options.DC) {
        amountOptions.style = "currency";
        amountOptions.currency = options.currency;
    }

    let lValue = Math.round(value * 100) / 100;
    let debitOrCredit = "";
    if (options.DC) {
        if (lValue > 0) {
            debitOrCredit = " D";
        } else {
            debitOrCredit = " C";
        }
        lValue = Math.abs(lValue);
    }
    let formattedValue = new Intl.NumberFormat("fr-FR", amountOptions).format(lValue);

    if (options.appendCharacter) {
        formattedValue += " " + options.appendCharacter;
    }

    if (options.DC) {
        formattedValue += debitOrCredit;
    }

    return formattedValue;
}
